import {
  DEFAULT_DYNAMICS,
  type BrushDynamics,
  type BrushPreset,
  type BrushPresetCategory,
  type BrushTip,
} from './types';

function tip(overrides: Partial<BrushTip> = {}): BrushTip {
  return {
    kind: overrides.imageId ? 'pattern' : 'circular',
    hardness: 1,
    angle: 0,
    roundness: 1,
    spacing: 0.12,
    imageId: null,
    ...overrides,
  };
}

function dynamics(overrides: Partial<BrushDynamics> = {}): BrushDynamics {
  return { ...DEFAULT_DYNAMICS, ...overrides };
}

function preset(
  id: string,
  name: string,
  category: BrushPresetCategory,
  size: number,
  tipValue: BrushTip,
  dynamicsValue: BrushDynamics = dynamics(),
): BrushPreset {
  return { id, name, category, size, tip: tipValue, dynamics: dynamicsValue };
}

/**
 * Built-in gallery: round basics plus dry/wet media, markers, pencils, and FX
 * so switching style is one click rather than a slider hunt.
 */
export const BUILTIN_PRESETS: BrushPreset[] = [
  // --- Basic ---
  preset('preset.soft-round', 'Soft Round', 'Basic', 40, tip({ hardness: 0, spacing: 0.1 })),
  preset('preset.hard-round', 'Hard Round', 'Basic', 24, tip({ hardness: 1, spacing: 0.08 })),
  preset(
    'preset.soft-pressure',
    'Soft Round Pressure',
    'Basic',
    60,
    tip({ hardness: 0.1, spacing: 0.06 }),
    dynamics({ pressureSize: true, pressureOpacity: true }),
  ),
  preset(
    'preset.flat-angled',
    'Flat Angled',
    'Basic',
    48,
    tip({ hardness: 0.9, roundness: 0.22, angle: 45, spacing: 0.05 }),
  ),
  preset('preset.square', 'Square', 'Basic', 32, tip({ imageId: 'tip.square', spacing: 0.1 })),
  preset(
    'preset.soft-oval',
    'Soft Oval',
    'Basic',
    55,
    tip({ hardness: 0.15, roundness: 0.45, angle: 20, spacing: 0.08 }),
  ),

  // --- Pencils ---
  preset('preset.pencil-hb', 'HB Pencil', 'Pencils', 4, tip({ hardness: 1, spacing: 0.04 })),
  preset(
    'preset.pencil-2b',
    '2B Pencil',
    'Pencils',
    6,
    tip({ hardness: 0.85, spacing: 0.05, roundness: 0.85 }),
    dynamics({ pressureOpacity: true, flowJitter: 0.08 }),
  ),
  preset(
    'preset.pencil-6b',
    '6B Soft Pencil',
    'Pencils',
    10,
    tip({ hardness: 0.55, spacing: 0.06, roundness: 0.7 }),
    dynamics({ pressureSize: true, pressureOpacity: true, flowJitter: 0.12 }),
  ),
  preset(
    'preset.pencil-mechanical',
    'Mechanical Pencil',
    'Pencils',
    2,
    tip({ hardness: 1, spacing: 0.03 }),
  ),
  preset(
    'preset.pencil-graphite',
    'Graphite Stick',
    'Pencils',
    28,
    tip({ imageId: 'tip.graphite', spacing: 0.06, roundness: 0.55, angle: -15 }),
    dynamics({ followDirection: true, flowJitter: 0.1 }),
  ),
  preset(
    'preset.pencil-hatch',
    'Hatching Pencil',
    'Pencils',
    36,
    tip({ imageId: 'tip.hatch', spacing: 0.12, angle: 35 }),
    dynamics({ followDirection: true, sizeJitter: 0.08 }),
  ),
  preset(
    'preset.pencil-sketch',
    'Sketch Pencil',
    'Pencils',
    8,
    tip({ hardness: 0.7, spacing: 0.05, roundness: 0.6, angle: -25 }),
    dynamics({ pressureSize: true, angleJitter: 0.08 }),
  ),
  preset(
    'preset.pencil',
    'Pencil',
    'Pencils',
    6,
    tip({ hardness: 1, spacing: 0.05 }),
  ),

  // --- Dry Media ---
  preset(
    'preset.chalk',
    'Chalk',
    'Dry Media',
    70,
    tip({ imageId: 'tip.chalk', spacing: 0.1 }),
    dynamics({ angleJitter: 0.4, sizeJitter: 0.1 }),
  ),
  preset(
    'preset.charcoal',
    'Charcoal',
    'Dry Media',
    80,
    tip({ imageId: 'tip.charcoal', spacing: 0.08 }),
    dynamics({ angleJitter: 0.6, flowJitter: 0.2 }),
  ),
  preset(
    'preset.charcoal-soft',
    'Soft Charcoal',
    'Dry Media',
    100,
    tip({ imageId: 'tip.charcoal', spacing: 0.1, roundness: 0.75 }),
    dynamics({ angleJitter: 0.7, sizeJitter: 0.15, flowJitter: 0.25 }),
  ),
  preset(
    'preset.conte',
    'Conte Crayon',
    'Dry Media',
    48,
    tip({ imageId: 'tip.conte', spacing: 0.07, roundness: 0.5, angle: 20 }),
    dynamics({ followDirection: true, flowJitter: 0.12 }),
  ),
  preset(
    'preset.crayon',
    'Crayon',
    'Dry Media',
    56,
    tip({ imageId: 'tip.crayon', spacing: 0.09 }),
    dynamics({ angleJitter: 0.25, sizeJitter: 0.12 }),
  ),
  preset(
    'preset.pastel',
    'Soft Pastel',
    'Dry Media',
    90,
    tip({ imageId: 'tip.pastel', spacing: 0.08 }),
    dynamics({ sizeJitter: 0.1, flowJitter: 0.15 }),
  ),
  preset(
    'preset.dry-bristle',
    'Dry Bristle',
    'Dry Media',
    64,
    tip({ imageId: 'tip.bristle', spacing: 0.05, roundness: 0.6 }),
    dynamics({ followDirection: true, flowJitter: 0.15 }),
  ),
  preset(
    'preset.dry-brush-rough',
    'Dry Brush Rough',
    'Dry Media',
    72,
    tip({ imageId: 'tip.bristle', spacing: 0.12, roundness: 0.45 }),
    dynamics({ followDirection: true, sizeJitter: 0.2, flowJitter: 0.35, scatter: 0.08 }),
  ),
  preset(
    'preset.dry-rag',
    'Dry Rag',
    'Dry Media',
    110,
    tip({ imageId: 'tip.rag', spacing: 0.2 }),
    dynamics({ angleJitter: 0.8, sizeJitter: 0.25, scatter: 0.15 }),
  ),
  preset(
    'preset.fan-dry',
    'Fan Dry',
    'Dry Media',
    96,
    tip({ imageId: 'tip.fan', spacing: 0.08, angle: 0 }),
    dynamics({ followDirection: true }),
  ),
  preset(
    'preset.stipple',
    'Stipple',
    'Dry Media',
    70,
    tip({ imageId: 'tip.stipple', spacing: 0.45 }),
    dynamics({ scatter: 0.35, sizeJitter: 0.3, angleJitter: 1 }),
  ),
  preset(
    'preset.sponge-dry',
    'Dry Sponge',
    'Dry Media',
    120,
    tip({ imageId: 'tip.sponge', spacing: 0.35 }),
    dynamics({ angleJitter: 1, sizeJitter: 0.25, scatter: 0.2 }),
  ),

  // --- Wet Media ---
  preset(
    'preset.wet-bristle',
    'Wet Bristle',
    'Wet Media',
    70,
    tip({ imageId: 'tip.wet-bristle', spacing: 0.04, roundness: 0.7 }),
    dynamics({ followDirection: true, flowJitter: 0.08 }),
  ),
  preset(
    'preset.oil-flat',
    'Oil Flat',
    'Wet Media',
    85,
    tip({ imageId: 'tip.oil-flat', spacing: 0.05, angle: 15 }),
    dynamics({ followDirection: true }),
  ),
  preset(
    'preset.oil-round',
    'Oil Round',
    'Wet Media',
    50,
    tip({ hardness: 0.35, spacing: 0.06 }),
    dynamics({ pressureSize: true, flowJitter: 0.1 }),
  ),
  preset(
    'preset.watercolor',
    'Watercolor Wash',
    'Wet Media',
    130,
    tip({ imageId: 'tip.watercolor', spacing: 0.18 }),
    dynamics({ sizeJitter: 0.2, scatter: 0.12, flowJitter: 0.2 }),
  ),
  preset(
    'preset.watercolor-detail',
    'Watercolor Detail',
    'Wet Media',
    40,
    tip({ imageId: 'tip.watercolor', spacing: 0.1 }),
    dynamics({ sizeJitter: 0.1, flowJitter: 0.15 }),
  ),
  preset(
    'preset.ink-wash',
    'Ink Wash',
    'Wet Media',
    95,
    tip({ imageId: 'tip.marker-fiber', spacing: 0.08 }),
    dynamics({ pressureOpacity: true, sizeJitter: 0.12 }),
  ),
  preset(
    'preset.wet-spatter',
    'Wet Spatter',
    'Wet Media',
    100,
    tip({ imageId: 'tip.spatter', spacing: 0.4 }),
    dynamics({ angleJitter: 1, scatter: 0.55, sizeJitter: 0.4, flowJitter: 0.25 }),
  ),
  preset(
    'preset.wet-sponge',
    'Wet Sponge',
    'Wet Media',
    140,
    tip({ imageId: 'tip.sponge', spacing: 0.25 }),
    dynamics({ angleJitter: 0.6, sizeJitter: 0.2, scatter: 0.1 }),
  ),

  // --- Markers ---
  preset(
    'preset.felt-fine',
    'Fine Felt Tip',
    'Markers',
    8,
    tip({ imageId: 'tip.felt', spacing: 0.05 }),
    dynamics({ pressureSize: false }),
  ),
  preset(
    'preset.felt-medium',
    'Felt Tip',
    'Markers',
    18,
    tip({ imageId: 'tip.felt', spacing: 0.06 }),
  ),
  preset(
    'preset.marker-round',
    'Round Marker',
    'Markers',
    28,
    tip({ imageId: 'tip.marker-fiber', spacing: 0.05 }),
  ),
  preset(
    'preset.marker-chisel',
    'Chisel Marker',
    'Markers',
    36,
    tip({ imageId: 'tip.chisel', spacing: 0.04, angle: -20 }),
    dynamics({ followDirection: true }),
  ),
  preset(
    'preset.marker-brush',
    'Brush Marker',
    'Markers',
    42,
    tip({ imageId: 'tip.brush-pen', spacing: 0.04 }),
    dynamics({ followDirection: true, pressureSize: true }),
  ),
  preset(
    'preset.highlighter',
    'Highlighter',
    'Markers',
    48,
    tip({ imageId: 'tip.highlighter', spacing: 0.05, angle: 0 }),
    dynamics({ followDirection: true, pressureSize: false }),
  ),
  preset(
    'preset.marker-bold',
    'Bold Marker',
    'Markers',
    40,
    tip({ hardness: 0.95, spacing: 0.05 }),
    dynamics({ pressureSize: false }),
  ),
  preset(
    'preset.permanent-marker',
    'Permanent Marker',
    'Markers',
    22,
    tip({ hardness: 1, spacing: 0.04, roundness: 0.85 }),
  ),

  // --- Ink & Calligraphy ---
  preset(
    'preset.calligraphy',
    'Calligraphy',
    'Ink & Calligraphy',
    36,
    tip({ hardness: 1, roundness: 0.12, angle: -30, spacing: 0.04 }),
    dynamics({ pressureSize: false }),
  ),
  preset(
    'preset.fountain',
    'Fountain Pen',
    'Ink & Calligraphy',
    10,
    tip({ imageId: 'tip.ink-nib', spacing: 0.03, angle: -35, roundness: 0.7 }),
    dynamics({ followDirection: true, pressureSize: true }),
  ),
  preset(
    'preset.dip-pen',
    'Dip Pen',
    'Ink & Calligraphy',
    8,
    tip({ imageId: 'tip.ink-nib', spacing: 0.025, angle: -40 }),
    dynamics({ followDirection: true, pressureSize: true, pressureOpacity: true }),
  ),
  preset(
    'preset.brush-pen',
    'Brush Pen',
    'Ink & Calligraphy',
    32,
    tip({ imageId: 'tip.brush-pen', spacing: 0.035 }),
    dynamics({ followDirection: true, pressureSize: true }),
  ),
  preset(
    'preset.ink-fine',
    'Fine Liner',
    'Ink & Calligraphy',
    3,
    tip({ hardness: 1, spacing: 0.03 }),
    dynamics({ pressureSize: false }),
  ),
  preset(
    'preset.sumi',
    'Sumi Brush',
    'Ink & Calligraphy',
    55,
    tip({ imageId: 'tip.wet-bristle', spacing: 0.04, roundness: 0.4 }),
    dynamics({ followDirection: true, pressureSize: true, flowJitter: 0.12 }),
  ),

  // --- Airbrush & Spray ---
  preset(
    'preset.airbrush-soft',
    'Soft Airbrush',
    'Airbrush & Spray',
    120,
    tip({ imageId: 'tip.airbrush', spacing: 0.08 }),
    dynamics({ pressureOpacity: true, pressureSize: true }),
  ),
  preset(
    'preset.airbrush-hard',
    'Hard Airbrush',
    'Airbrush & Spray',
    80,
    tip({ hardness: 0.25, spacing: 0.06 }),
    dynamics({ pressureOpacity: true }),
  ),
  preset(
    'preset.spray-paint',
    'Spray Paint',
    'Airbrush & Spray',
    100,
    tip({ imageId: 'tip.spray', spacing: 0.3 }),
    dynamics({ scatter: 0.4, sizeJitter: 0.35, angleJitter: 1, flowJitter: 0.2 }),
  ),
  preset(
    'preset.spatter',
    'Spatter',
    'Airbrush & Spray',
    90,
    tip({ imageId: 'tip.spatter', spacing: 0.35 }),
    dynamics({ angleJitter: 1, scatter: 0.45, sizeJitter: 0.35 }),
  ),
  preset(
    'preset.mist',
    'Mist Spray',
    'Airbrush & Spray',
    160,
    tip({ imageId: 'tip.airbrush', spacing: 0.15 }),
    dynamics({ scatter: 0.25, sizeJitter: 0.2, flowJitter: 0.3 }),
  ),

  // --- Texture & FX ---
  preset(
    'preset.grunge',
    'Grunge Texture',
    'Texture & FX',
    140,
    tip({ imageId: 'tip.grunge', spacing: 0.4 }),
    dynamics({ angleJitter: 1, sizeJitter: 0.3, scatter: 0.2 }),
  ),
  preset(
    'preset.stars',
    'Star Scatter',
    'Texture & FX',
    40,
    tip({ imageId: 'tip.star', spacing: 0.9 }),
    dynamics({ angleJitter: 1, scatter: 0.8, sizeJitter: 0.5 }),
  ),
  preset(
    'preset.leaves',
    'Leaf Scatter',
    'Texture & FX',
    50,
    tip({ imageId: 'tip.leaf', spacing: 0.85 }),
    dynamics({ angleJitter: 1, scatter: 0.7, sizeJitter: 0.4 }),
  ),
  preset(
    'preset.triangles',
    'Triangle Scatter',
    'Texture & FX',
    36,
    tip({ imageId: 'tip.triangle', spacing: 0.8 }),
    dynamics({ angleJitter: 1, scatter: 0.65, sizeJitter: 0.35 }),
  ),
  preset(
    'preset.noise-stamp',
    'Noise Stamp',
    'Texture & FX',
    100,
    tip({ imageId: 'tip.grunge', spacing: 0.55 }),
    dynamics({ angleJitter: 1, sizeJitter: 0.15 }),
  ),
];

/** Stable display order for gallery sections. */
export const BRUSH_PRESET_CATEGORIES: BrushPresetCategory[] = [
  'Basic',
  'Pencils',
  'Dry Media',
  'Wet Media',
  'Markers',
  'Ink & Calligraphy',
  'Airbrush & Spray',
  'Texture & FX',
];
